import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import * as yup from 'yup'
import {
	type AssociationBoardMembershipCreateRequest,
	type AssociationBoardMembershipUpdateRequest,
	type AssociationMembershipResponse,
	Gender,
} from '~~/openapi-client/gen/schemas'
import { useCommonMemberFields } from '~/composables/useCommonMemberFields'
import type { MemberTag } from '~~/types/MemberTag'

export const useAssociationBoardMembershipForm = () => {
	const { selectedAssociation, refresh: refreshAssociations } = useAssociations()
	const { createdAssociation, refresh: refreshCreatorAssociations } = useCreatorAssociations()
	const { refresh: refreshMembership } = useUserMembership()
	const { $api } = useNuxtApp()

	const boardMember = ref<AssociationMembershipResponse>()

	const schema = toTypedSchema(yup.object({
		associationId: yup.string().required(),
		firstname: yup.string().required('Der Vorname ist erforderlich.'),
		lastname: yup.string().required('Der Nachname ist erforderlich.'),
		birthday: yup.string().required('Das Geburtsdatum ist erforderlich.'),
		gender: yup.mixed<Gender>().oneOf(Object.values(Gender), 'Das Geschlecht ist ungültig.').required(),
		iban: yup.string().required('Die IBAN ist erforderlich.').matches(ibanRegExp, 'Die IBAN ist ungültig.'),
		joinDate: yup.string().required('Das Beitrittsdatum ist erforderlich.'),
		contactInformation: yup.object({
			address: yup.object({
				street: yup.string().required('Die Straße ist erforderlich.'),
				houseNumber: yup.string().required('Die Hausnummer ist erforderlich.'),
				postalCode: yup.string().required('Die Postleitzahl ist erforderlich.').matches(plzRegExp, 'Die PLZ ist ungültig.'),
				city: yup.string().required('Die Stadt ist erforderlich.'),
			}),
			phone: yup.string().matches(phoneRegExp, 'Die Telefonnummer ist ungültig.').required('Die Telefonnummer ist erforderlich.'),
			email: yup.string().email().required('Die E-Mail-Adresse ist erforderlich.'),
		}),
		tags: yup.array().of(
			yup.object({
				name: yup.string().required(),
			}),
		).required(),
	}))

	const {
		handleSubmit,
		errors,
		defineField,
		validateField,
		meta,
		resetForm,
	} = useForm({ validationSchema: schema })

	const memberFields = useCommonMemberFields(defineField, validateField)

	/**
	 * Initialisiert das Formular mit den Daten des Vorstandsmitglieds mit der angegebenen ID.
	 * Wenn keine ID angegeben ist, werden die initialen Daten (aus dem Keycloak) des aktuellen Benutzers geladen.
	* @param id
	*/
	const init = async (id?: string) => {
		let response
		if (id) {
			response = await $api.associationMembershipApi.findById(id)
			boardMember.value = response.data
			resetForm({
				values: {
					associationId: (createdAssociation.value?.id || selectedAssociation.value?.id),
					firstname: boardMember.value.firstname,
					lastname: boardMember.value.lastname,
					birthday: boardMember.value.birthday,
					gender: boardMember.value.gender,
					contactInformation: {
						address: {
							street: boardMember.value.contactInformationResponse?.address.street,
							houseNumber: boardMember.value.contactInformationResponse?.address.houseNumber,
							postalCode: boardMember.value.contactInformationResponse?.address.postalCode,
							city: boardMember.value.contactInformationResponse?.address.city,
						},
						email: boardMember.value.contactInformationResponse?.email,
						phone: boardMember.value.contactInformationResponse?.phone,
					},
					iban: boardMember.value.iban,
					joinDate: boardMember.value.joinDate,
					tags: boardMember.value.tags,
				},
			})
		} else {
			const response = (await $api.accountApi.findWithInitialData()).data
			resetForm({
				values: {
					associationId: (createdAssociation.value?.id || selectedAssociation.value?.id),
					firstname: response.firstname,
					lastname: response.lastname,
					birthday: response.birthday,
					gender: response.gender,
					contactInformation: {
						address: {
							street: response.street,
							houseNumber: response.houseNumber,
							postalCode: response.postalCode,
							city: response.city,
						},
						email: response.email,
						phone: response.phone,
					},
					tags: [],
				},
			})
		}
	}

	const create = () => handleSubmit(async (formValues: AssociationBoardMembershipCreateRequest) => {
		// Remove spaces from IBAN before sending to API
		formValues.iban = normalizeIban(formValues.iban)!

		await $api.associationMembershipApi.createBoardMembership(formValues)
		await refreshAssociations()
		await refreshCreatorAssociations()
		await refreshMembership()
	})()

	const update = (id: string) => handleSubmit(async (formValues: AssociationBoardMembershipUpdateRequest) => {
		const userDefinedTags = (formValues.tags ?? [])
			.filter((tag: MemberTag) => tag.type !== 'FUNCTIONAL')
			.map(tag => ({ name: tag.name }))

		// Remove spaces from IBAN before sending to API
		formValues.iban = normalizeIban(formValues.iban)!

		await $api.associationMembershipApi.updateBoardMembership(id, {
			...formValues,
			tags: userDefinedTags,
		})

		await refreshMembership()
	})()

	return {
		memberFields,
		errors,
		meta,
		resetForm,
		init,
		create,
		update,
	}
}
